import { ASSET_CACHE_NAME } from '../config';

// Set once per tab session before reloading, so a genuinely broken deploy can't trigger an infinite
// reload loop. Cleared by `clearStaleBuildRecovery` after the next init succeeds.
const RECOVERY_FLAG = 'stale-build-recovery';
// The flag above only guards a boot that never comes up. A boot that comes up, clears it and then
// fails again (a worker that crashes after `initApi`, a chunk that keeps failing on a flaky network)
// re-armed the recovery every few seconds: wipe, reload, wipe, reload — the page never stayed up long
// enough to paint, which is the "white screen that keeps flashing" reported from the field. Budget the
// recoveries per tab session instead: a build that is genuinely stale is fixed by the first reload,
// so anything past a couple inside the window is not a stale build and must not reload again.
const RECOVERY_ATTEMPTS_KEY = 'stale-build-recovery-attempts';
const MAX_RECOVERY_ATTEMPTS = 2;
const RECOVERY_ATTEMPT_WINDOW = 10 * 60 * 1000;
// Not handled by the Service Worker, so only the server itself can answer it. The static host answers
// a missing file with `index.html`, so the content type tells a real answer from the SPA fallback.
const REACHABILITY_PROBE_URL = '/version.txt';
const REACHABILITY_PROBE_CONTENT_TYPE = 'text/plain';
const REACHABILITY_PROBE_TIMEOUT = 5000;
const REACHABILITY_RETRY_MIN_DELAY = 3000;
const REACHABILITY_RETRY_MAX_DELAY = 15000;

let isRecovering = false;

// After a deploy purges the old hashed chunks, a long-lived tab that respawns the API worker (or
// lazy-loads any chunk) fetches a now-missing URL and silently hangs — requests queue forever and
// the UI looks alive but does nothing. Drop the cached old bundle and the controlling Service Worker
// so the forced reload pulls the freshly deployed assets straight from the network.
export async function recoverFromStaleBuild() {
  if (isRecovering || getRecoveryFlag()) return false;

  const attempts = readRecoveryAttempts();
  if (attempts >= MAX_RECOVERY_ATTEMPTS) {
    // eslint-disable-next-line no-console
    console.error('[Recovery] Stale-build recovery budget exhausted, not reloading again');
    return false;
  }
  isRecovering = true;

  // Offline, a chunk or the worker fails to load just the same. Wiping the cache and the worker and
  // then reloading would leave nothing to boot from: Safari shows its error page and the desktop app
  // stays blank, even after the network returns.
  await waitForServer();
  // Set only now: a flag left over from the wait would survive a reload made some other way and
  // disable the recovery of the next boot
  setRecoveryFlag();
  writeRecoveryAttempts(attempts + 1);

  try {
    await self.caches?.delete(ASSET_CACHE_NAME);
  } catch {
    // Ignore — a missing CacheStorage still lets the reload proceed
  }

  try {
    const registrations = await navigator.serviceWorker?.getRegistrations();
    await Promise.all((registrations ?? []).map((registration) => registration.unregister()));
  } catch {
    // Ignore — unregistering is best-effort
  }

  window.location.reload();
  return true;
}

export function clearStaleBuildRecovery() {
  try {
    sessionStorage.removeItem(RECOVERY_FLAG);
  } catch {
    // Ignore — private-mode storage failures are non-fatal here
  }
}

async function waitForServer() {
  let delay = REACHABILITY_RETRY_MIN_DELAY;

  while (!await checkServerReachable()) {
    await waitForRetry(delay);
    delay = Math.min(delay * 2, REACHABILITY_RETRY_MAX_DELAY);
  }
}

// Resolves after `delay`, or as soon as the browser reports the network is back
function waitForRetry(delay: number) {
  return new Promise<void>((resolve) => {
    function handleOnline() {
      window.clearTimeout(timeout);
      resolve();
    }

    const timeout = window.setTimeout(() => {
      window.removeEventListener('online', handleOnline);
      resolve();
    }, delay);
    window.addEventListener('online', handleOnline, { once: true });
  });
}

async function checkServerReachable() {
  const controller = new AbortController();
  const timeout = window.setTimeout(() => controller.abort(), REACHABILITY_PROBE_TIMEOUT);

  try {
    const response = await fetch(REACHABILITY_PROBE_URL, { cache: 'no-store', signal: controller.signal });
    return response.ok && Boolean(response.headers.get('content-type')?.startsWith(REACHABILITY_PROBE_CONTENT_TYPE));
  } catch {
    return false;
  } finally {
    window.clearTimeout(timeout);
  }
}

function readRecoveryAttempts() {
  try {
    const raw = sessionStorage.getItem(RECOVERY_ATTEMPTS_KEY);
    if (!raw) return 0;
    const parsed = JSON.parse(raw) as { count?: number; at?: number };
    if (!parsed.at || Date.now() - parsed.at > RECOVERY_ATTEMPT_WINDOW) return 0;
    return parsed.count || 0;
  } catch {
    return 0;
  }
}

function writeRecoveryAttempts(count: number) {
  try {
    sessionStorage.setItem(RECOVERY_ATTEMPTS_KEY, JSON.stringify({ count, at: Date.now() }));
  } catch {
    // Ignore — without storage the budget degrades to the once-per-boot flag above
  }
}

function getRecoveryFlag() {
  try {
    return Boolean(sessionStorage.getItem(RECOVERY_FLAG));
  } catch {
    return false;
  }
}

function setRecoveryFlag() {
  try {
    sessionStorage.setItem(RECOVERY_FLAG, '1');
  } catch {
    // Ignore — without storage the once-per-session guard degrades but recovery still runs once
  }
}
